import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Select, { MultiValue } from 'react-select';
import { useAuthContext } from '../../hooks/auth';
import { useClearAppCachesOnMount } from '../../hooks/useClearAppCachesOnMount';
import { displayError } from '../../utils/messageToast';
import { InputsInfosPages } from '../../components/ui/inputs';
import { generatePlan } from '../../functions/generatePlan';
import { fetchAllTabs } from '../../functions/getAllTabs';

export type TabulationOption = {
  value: string;
  label: string;
  tabName: string;
  skillName: string;
};

export default function Plan() {
  useClearAppCachesOnMount();
  const { user, authorizationForce, authorization } = useAuthContext();
  const ambient = user?.ambient as string;
  const isInForce = Boolean(user?.inForce);
  const token = isInForce ? authorizationForce : authorization;

  const [options, setOptions] = useState<TabulationOption[]>([]);
  const [tabsLoading, setTabsLoading] = useState(false);
  const [selected, setSelected] = useState<MultiValue<TabulationOption>>([]);
  const [planLoading, setPlanLoading] = useState(false);
  const [workbookName, setWorkbookName] = useState('');

  useEffect(() => {
    if (!ambient?.trim() || !token?.trim()) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setTabsLoading(true);
      const rows = await fetchAllTabs(ambient, token, isInForce);
      if (cancelled) return;
      setTabsLoading(false);
      if (!rows?.length) {
        displayError('Não foi possível carregar a lista de tabulações.');
        setOptions([]);
        return;
      }
      const next: TabulationOption[] = rows
        .map((t) => {
          const skillPart = (t.skill_name ?? t.skill_id ?? '').trim();
          if (!skillPart) return null;
          const tabName = (t.name ?? '').trim() || t.id;
          const label = `${tabName} - ${skillPart}`;
          return {
            value: t.id,
            label,
            tabName,
            skillName: (t.skill_name ?? '').trim() || skillPart,
          };
        })
        .filter((x): x is TabulationOption => x != null);
      if (!next.length) {
        displayError(
          'Nenhuma tabulação com skill associada na listagem. Verifique a API get-all-tabs.'
        );
      }
      setOptions(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [isInForce, ambient, token]);

  const selectionForApi = useMemo(
    () =>
      selected.map((item) => ({
        id: item.value,
        skillName: item.skillName,
        tabName: item.tabName,
      })),
    [selected]
  );

  return (
    <div className="bg-page">
      <Helmet>
        <title>Planilha por tabulações</title>
      </Helmet>

      <div className="bg-page-tabulation w-full px-2 sm:px-4 md:px-6 py-4 payload-page">
        <div className="form-page-shell">
          <div className="form-config-card">
            <div className="form-card-header">
              <div>
                <h2 className="form-card-title">Gerar planilha de tabulações</h2>
                <p className="form-card-subtitle">
                  Selecione uma ou mais tabulações.
                </p>
              </div>
            </div>

            <div className="form-card-body">
              <div className="plan-page-body">
                <div className="form-info-row">
                  <label className="form-info-label" htmlFor="plan-select-tabs">
                    Selecione as tabulações
                  </label>
                  <div className="plan-page-select-shell">
                    <Select<TabulationOption, true>
                      inputId="plan-select-tabs"
                      isMulti
                      isLoading={tabsLoading}
                      isDisabled={!token?.trim() || !ambient?.trim() || tabsLoading}
                      classNamePrefix="plan-select"
                      className="plan-select-root"
                      placeholder={
                        !token?.trim() || !ambient?.trim()
                          ? 'Faça login para carregar as tabulações'
                          : tabsLoading
                            ? 'Carregando tabulações…'
                            : options.length === 0
                              ? 'Nenhuma tabulação retornada pela API'
                              : 'Selecione uma ou mais (tab - skill)'
                      }
                      options={options}
                      value={selected}
                      onChange={(next) => setSelected(next ?? [])}
                      filterOption={(candidate, input) => {
                        const q = input.trim().toLowerCase();
                        if (!q) return true;
                        const o = candidate.data as TabulationOption;
                        return (
                          o.label.toLowerCase().includes(q) ||
                          o.value.toLowerCase().includes(q) ||
                          o.skillName.toLowerCase().includes(q) ||
                          o.tabName.toLowerCase().includes(q)
                        );
                      }}
                      noOptionsMessage={() =>
                        tabsLoading ? 'Carregando…' : 'Nenhuma opção'
                      }
                      styles={{
                        container: (base) => ({ ...base, width: '100%' }),
                        control: (base) => ({ ...base, width: '100%' }),
                      }}
                    />
                  </div>
                </div>

                <div className="form-info-row">
                  <label className="form-info-label" id="plan-selected-ids-label">
                    Tabulações selecionadas
                  </label>
                  <div
                    className="plan-selected-ids"
                    role="region"
                    aria-labelledby="plan-selected-ids-label"
                  >
                    {selectionForApi.length === 0 ? (
                      <p className="plan-selected-ids-empty">
                        Nenhuma tabulação selecionada.
                      </p>
                    ) : (
                      <ul>
                        {selectionForApi.map((row) => (
                          <li key={row.id}>
                            <span className="plan-selected-id">{row.id}</span>
                            <span className="plan-selected-meta">
                              {' '}
                              · {row.tabName} — {row.skillName}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="form-info-row">
                  <label className="form-info-label" htmlFor="plan-workbook-name">
                    Nome do arquivo (.xlsx)
                  </label>
                  <div className="plan-page-select-shell">
                    <InputsInfosPages
                      id="plan-workbook-name"
                      type="text"
                      placeholder="Ex.: Tabulacoes_Allianz_marco"
                      value={workbookName}
                      onChange={(e) => setWorkbookName(e.target.value)}
                      disabled={!token?.trim()}
                      autoComplete="off"
                    />
                  </div>
                  <p className="create-protocol-modal-hint" style={{ marginTop: '0.35rem' }}>
                    Não é necessário digitar .xlsx. Caracteres inválidos serão ajustados no servidor.
                  </p>
                </div>

                <div className="plan-page-actions">
                  <button
                    type="button"
                    className="modalButton"
                    disabled={
                      planLoading ||
                      selectionForApi.length === 0 ||
                      !token?.trim() ||
                      !workbookName.trim()
                    }
                    onClick={() => {
                      setPlanLoading(true);
                      void generatePlan(
                        selectionForApi,
                        workbookName,
                        ambient,
                        token,
                        isInForce ? 'force' : 'rc'
                      )
                        .then((ok) => {
                          if (ok) {
                            // setSelected([]);
                            setWorkbookName('');
                          }
                        })
                        .finally(() => setPlanLoading(false));
                    }}
                  >
                    {planLoading ? 'Gerando…' : 'Gerar planilha'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
